/**
 * KAN-746: позиции заказа: картинка, название (ссылка на товар каталога,
 * если позиция с ним связана), артикул, цена или "цена по запросу",
 * количество, сумма. В редактируемом заказе количество меняется полем
 * (сохраняется по Enter или уходу из поля), позицию можно удалить.
 * Широкая таблица прокручивается внутри своей рамки.
 * KAN-755: позиция со скидкой: сумма без скидки зачёркнута, ниже сумма со
 * скидкой и название скидки.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ProductThumb } from '@/components/catalog/ProductThumb';
import { formatRubKopecks } from '@/lib/money';
import type { OrderItem } from '@/types/order';
import { discountAmount, toAmount } from './orderFormat';

export const PRICE_ON_REQUEST = 'цена по запросу';

interface OrderItemsTableProps {
  items: OrderItem[];
  editable: boolean;
  /** Идёт сохранение правки: поля и кнопки неактивны. */
  pending?: boolean;
  onSetQuantity: (index: number, quantity: number) => void;
  onRemove: (index: number) => void;
}

function hasPrice(item: OrderItem): boolean {
  return !item.price_unconfirmed && item.price !== null && item.price !== undefined && item.price !== '';
}

function LineTotal({ item }: { item: OrderItem }) {
  const total = toAmount(item.total);
  const discount = discountAmount(item.discount);
  if (discount === null) return <>{formatRubKopecks(total)}</>;
  return (
    <div className="flex flex-col items-end">
      <s className="text-xs font-normal text-gray-400" data-testid={`order-item-total-full-${item.index}`}>
        {formatRubKopecks(total)}
      </s>
      <span className="text-green-700" data-testid={`order-item-total-discounted-${item.index}`}>
        {formatRubKopecks(Math.max(0, total - discount))}
      </span>
      {item.discount_name && (
        <span className="max-w-[180px] truncate text-xs font-normal text-gray-500" title={item.discount_name}>
          {item.discount_name}
        </span>
      )}
    </div>
  );
}

function QuantityField({
  item,
  disabled,
  onCommit,
}: {
  item: OrderItem;
  disabled: boolean;
  onCommit: (quantity: number) => void;
}) {
  const [value, setValue] = useState(String(item.quantity));
  useEffect(() => setValue(String(item.quantity)), [item.quantity]);

  const commit = () => {
    const quantity = Math.floor(Number(value));
    if (!Number.isFinite(quantity) || quantity < 1) {
      setValue(String(item.quantity));
      return;
    }
    if (quantity !== item.quantity) onCommit(quantity);
  };

  return (
    <Input
      type="number"
      min={1}
      step={1}
      inputMode="numeric"
      aria-label={`Количество: ${item.name}`}
      data-testid={`order-item-qty-input-${item.index}`}
      className="ml-auto h-8 w-20 text-right"
      value={value}
      disabled={disabled}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
}

export function OrderItemsTable({ items, editable, pending = false, onSetQuantity, onRemove }: OrderItemsTableProps) {
  return (
    <div className="overflow-x-auto">
      <Table aria-label="Позиции заказа" data-testid="order-items-table">
        <TableHeader>
          <TableRow>
            <TableHead className="w-14" />
            <TableHead>Товар</TableHead>
            <TableHead>Артикул</TableHead>
            <TableHead className="text-right">Цена</TableHead>
            <TableHead className="text-right">Кол-во</TableHead>
            <TableHead className="text-right">Сумма</TableHead>
            {editable && <TableHead className="w-12" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const priced = hasPrice(item);
            return (
              <TableRow key={item.index} data-testid={`order-item-${item.index}`}>
                <TableCell className="py-2">
                  <ProductThumb src={item.picture_url} alt="" className="h-10 w-10" />
                </TableCell>
                <TableCell className="min-w-[200px] max-w-[360px]" data-testid={`order-item-name-${item.index}`}>
                  {item.product_id !== null && item.product_id !== undefined && item.product_id !== '' ? (
                    <Link
                      to={`/catalog/products/${encodeURIComponent(String(item.product_id))}`}
                      className="line-clamp-2 font-medium text-blue-600 hover:underline"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <span className="line-clamp-2 font-medium">{item.name}</span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm text-gray-600">{item.sku || '-'}</TableCell>
                <TableCell
                  className="whitespace-nowrap text-right text-sm"
                  data-testid={`order-item-price-${item.index}`}
                >
                  {priced ? formatRubKopecks(toAmount(item.price)) : (
                    <span className="text-amber-700">{PRICE_ON_REQUEST}</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums" data-testid={`order-item-qty-${item.index}`}>
                  {editable ? (
                    <QuantityField
                      item={item}
                      disabled={pending}
                      onCommit={(quantity) => onSetQuantity(item.index, quantity)}
                    />
                  ) : (
                    item.quantity
                  )}
                </TableCell>
                <TableCell
                  className="whitespace-nowrap text-right text-sm font-medium"
                  data-testid={`order-item-total-${item.index}`}
                >
                  {priced ? <LineTotal item={item} /> : '-'}
                </TableCell>
                {editable && (
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Удалить: ${item.name}`}
                      data-testid={`order-item-remove-${item.index}`}
                      disabled={pending}
                      onClick={() => onRemove(item.index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
